import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { Subscription, filter } from "rxjs";

import {
  AccionFlotanteCc,
  DURACION_FLOTANTE_CC,
  DatosFlotanteCc,
  MAXIMO_FLOTANTES_CC,
  POSICION_FLOTANTES_CC,
  RAIZ_CALIDAD_CODIGO,
  TipoFlotanteCc,
} from "./flotante-cc";
import { FlotanteCcComponent } from "./flotante-cc.component";

/** Lo que se le puede añadir a un mensaje. */
export interface OpcionesFlotanteCc {
  /** Un enlace dentro del mensaje (su texto y su ruta). */
  accion?: AccionFlotanteCc;
  /** Un icono propio; sin él, el del tipo. */
  icono?: string;
}

/**
 * Los mensajes del módulo como flotantes arriba a la derecha, bajo la barra del
 * COS, igual que los demás avisos del COS: se van solos a los 15 segundos, con
 * botón para cerrarlos antes y una barra que marca el tiempo. Usa el
 * ToastrService global del COS con sus opciones en cada llamada, así que los
 * demás módulos siguen con las suyas. El mismo mensaje no se apila dos veces,
 * caben a lo más cuatro a la vez (el más nuevo arriba) y, al salir del módulo,
 * se cierran.
 */
@Injectable({ providedIn: "root" })
export class FlotantesCcService implements OnDestroy {
  private activos: ActiveToast<FlotanteCcComponent>[] = [];
  private readonly ruta$: Subscription;

  constructor(
    private readonly toastr: ToastrService,
    router: Router,
  ) {
    this.ruta$ = router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (!e.urlAfterRedirects.startsWith(RAIZ_CALIDAD_CODIGO)) {
          this.cierraTodos();
        }
      });
  }

  ngOnDestroy(): void {
    this.ruta$.unsubscribe();
  }

  exito(texto: string, opciones: OpcionesFlotanteCc = {}): number {
    return this.muestra("exito", texto, opciones);
  }

  info(texto: string, opciones: OpcionesFlotanteCc = {}): number {
    return this.muestra("info", texto, opciones);
  }

  aviso(texto: string, opciones: OpcionesFlotanteCc = {}): number {
    return this.muestra("aviso", texto, opciones);
  }

  error(texto: string, opciones: OpcionesFlotanteCc = {}): number {
    return this.muestra("error", texto, opciones);
  }

  /** Pinta un mensaje y devuelve su id (para cerrarlo antes con cierra). */
  muestra(tipo: TipoFlotanteCc, texto: string, opciones: OpcionesFlotanteCc = {}): number {
    // El mismo texto no se repite: el anterior se va y el nuevo llega con su tiempo completo.
    for (const igual of this.activos.filter((a) => a.message === texto)) {
      this.cierra(igual.toastId);
    }
    while (this.activos.length >= MAXIMO_FLOTANTES_CC) {
      this.cierra(this.activos[0].toastId);
    }
    const payload: DatosFlotanteCc = { tipo, accion: opciones.accion ?? null, icono: opciones.icono ?? null };
    const flotante: ActiveToast<FlotanteCcComponent> = this.toastr.show<DatosFlotanteCc>(texto, undefined, {
      toastComponent: FlotanteCcComponent,
      positionClass: POSICION_FLOTANTES_CC,
      toastClass: "cc-flotante",
      timeOut: DURACION_FLOTANTE_CC,
      disableTimeOut: false,
      closeButton: true,
      progressBar: true,
      tapToDismiss: false,
      enableHtml: false,
      newestOnTop: true,
      payload,
    });
    this.activos.push(flotante);
    flotante.onHidden.subscribe(() => this.quita(flotante.toastId));
    return flotante.toastId;
  }

  /** Cierra un mensaje (con su salida). */
  cierra(id: number): void {
    this.quita(id);
    this.toastr.clear(id);
  }

  /** Cierra los mensajes del módulo; los de otros módulos se quedan. (cierra arma una lista nueva: esta no cambia al recorrerla.) */
  cierraTodos(): void {
    for (const a of this.activos) {
      this.cierra(a.toastId);
    }
  }

  private quita(id: number): void {
    this.activos = this.activos.filter((a) => a.toastId !== id);
  }
}
